import React, { useState } from 'react';
import PropTypes from 'prop-types';

/**
 * IprokWindow — React компонент для презентации мембранного вікна Iprok
 * Тексти виконані українською. Компонент адаптивний, оформлений Tailwind-класами
 * (якщо Tailwind відсутній — в компоненті є прості inline-стилі, щоб вигляд був прийнятним).
 *
 * Props:
 *  - title (string) — заголовок продукту
 *  - subtitle (string) — підзаголовок
 *  - images (array|string) — масив шляхів до зображень або CSV-рядок
 *  - video (string) — шлях до відео (mp4)
 *  - price (string|number) — ціна (опціонально)
 *  - ctaText (string) — текст для кнопки CTA
 *  - onCta (function) — колбек при натисканні на CTA
 */

export default function IprokWindow({
  title = 'Мембранне вікно Iprok',
  subtitle = 'Швидка захисна модульна система для вікон',
  images = ['/prok1.jpg', '/prok2.jpg'],
  video = '/video/demo.mp4',
  price = '',
  ctaText = 'Додати в кошик',
  onCta = null,
  fallbackStyles
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);

  // normalize images prop
  const imgs = Array.isArray(images)
    ? images
    : typeof images === 'string'
      ? images.split(',').map(s => s.trim()).filter(Boolean)
      : [];

  const mediaItems = [];
  if (imgs.length) imgs.forEach(src => mediaItems.push({ type: 'image', src }));
  if (video) mediaItems.push({ type: 'video', src: video });

  const current = mediaItems[currentIndex] || mediaItems[0];

    // Получить превью для видео (первое изображение или дефолтная картинка)
    const videoPreview = imgs[0] || '/img/main2.png';

  // Навигация по карусели
  const goPrev = () => setCurrentIndex((prev) => (prev === 0 ? mediaItems.length - 1 : prev - 1));
  const goNext = () => setCurrentIndex((prev) => (prev === mediaItems.length - 1 ? 0 : prev + 1));

  // Открыть/закрыть модальное окно
  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  // Bootstrap-like styles
  const bs = {
    container: {
      margin: '32px auto',
      maxWidth: 1200,
      padding: '0 16px',
      position: 'relative',
        gridColumn: 'span 2',
    },
    row: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 32,
    },
    col: {
      flex: 1,
      minWidth: 320,
      padding: '18px 8px',
    },
    h2: {
      fontFamily: 'Inter, Arial, sans-serif',
      color: '#2e8b57',
      fontWeight: 600,
      fontSize: 28,
      marginBottom: 8,
      letterSpacing: 0.5,
    },
    subtitle: {
      fontSize: 17,
      color: '#444',
      marginBottom: 8,
    },
    p: {
      color: '#333',
      lineHeight: 1.55,
      fontSize: 16,
      marginBottom: 12,
    },
    ul: {
      marginLeft: 0,
      paddingLeft: 0,
      fontSize: 15,
      color: '#222',
      listStyle: 'none',
      marginTop: 12,
    },
    li: {
      marginBottom: 8,
    },
    mediaWrap: {
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
      position: 'relative',
      marginTop: 20,
    },
    mediaMain: {
      borderRadius: 16,
      overflow: 'hidden',
      boxShadow: '0 2px 16px rgba(46,139,87,0.08)',
      position: 'relative',
      minHeight: 590,
      background: '#f8fafb',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    navBtn: {
      position: 'absolute',
      top: '45%',
      background: 'rgba(255,255,255,0.85)',
      borderRadius: '50%',
      border: 'none',
      width: 36,
      height: 36,
      fontSize: 22,
      cursor: 'pointer',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      zIndex: 2,
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      background: 'rgba(0,0,0,0.85)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background 0.2s',
    },
    modalContent: {
      position: 'relative',
      maxWidth: '90vw',
      maxHeight: '90vh',
      background: 'none',
      borderRadius: 12,
      boxShadow: '0 8px 32px rgba(46,139,87,0.18)',
      overflow: 'hidden',
    },
    modalClose: {
      position: 'absolute',
      top: 12,
      right: 12,
      zIndex: 2,
      background: 'rgba(255,255,255,0.85)',
      borderRadius: 8,
      border: 'none',
      padding: '8px 16px',
      cursor: 'pointer',
      fontWeight: 600,
      fontSize: 18,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    },
    thumbs: {
      marginTop: 8,
      display: 'flex',
      gap: 12,
      overflowX: 'auto',
    },
    thumbBtn: (active) => ({
      flexShrink: 0,
      borderRadius: 8,
      overflow: 'hidden',
      border: active ? '2px solid #2e8b57' : '1px solid #ddd',
      boxShadow: active ? '0 2px 8px rgba(46,139,87,0.12)' : 'none',
      width: 90,
      height: 70,
      background: active ? '#f3f8f6' : '#fff',
      transition: 'border 0.2s, box-shadow 0.2s',
      padding: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }),
    thumbImg: {
      height: 70,
      borderRadius: 8,
      objectFit: 'cover',
    },
    videoThumb: (active) => ({
      ...bs.thumbBtn(active),
      background: active ? '#2e8b57' : '#111',
      color: '#fff',
      fontWeight: 700,
      fontSize: 15,
      letterSpacing: 1,
    }),
    infoBox: {
      marginTop: 18,
      padding: 12,
      borderRadius: 8,
      background: 'linear-gradient(90deg, rgba(46,139,87,0.08), rgba(46,139,87,0.02))',
      fontWeight: 600,
      fontSize: 16,
      color: '#2e8b57',
    },
  };

  return (
    <div style={bs.container} className="iprok-wrap">
      <div style={bs.row}>
        <div style={bs.col}>
          <h2 style={bs.h2}>{title}</h2>
          <div style={bs.subtitle}>{subtitle}</div>
          <p style={bs.p}>
            <strong>Iprok</strong> — інноваційне мембранне вікно, створене для екстрених ситуацій. Модульна конструкція дозволяє швидко та надійно встановлювати захисний модуль замість розбитого скла або перед існуючим вікном для додаткового бар'єру.
          </p>
          <ul style={bs.ul}>
            <li style={bs.li}>✅ Модульний та легкий монтаж — заміна займає хвилини.</li>
            <li style={bs.li}>✅ Стійкість до ударної хвилі та локального тиску</li>
            <li style={bs.li}>✅ Захищає від уламків та зменшує ризики для мешканців.</li>
            <li style={bs.li}>✅ Може бути змонтоване перед реальним вікном як додатковий захист.</li>
            <li style={bs.li}>✅ Розроблено та випробувано з урахуванням умов бойових дій.</li>
          </ul>
          <div style={bs.infoBox}>
            Захисти свою домівку від війни — <em>Iprok</em>
          </div>
          {/* MEDIA */}
          <div style={bs.mediaWrap} className='media-wrap'>
            <div style={bs.mediaMain}>
              <button
                onClick={openModal}
                style={{ ...bs.navBtn, top: 12, right: 12, borderRadius: 8, width: 'auto', height: 'auto', fontSize: 15, padding: '6px 12px' }}
                aria-label="Открыть на весь экран"
              >
                ⤢
              </button>
              {current && current.type === 'image' ? (
                <img
                  src={current.src}
                  alt={`${title} — фото ${currentIndex + 1}`}
                  style={{ ...bs.thumbImg, ...fallbackStyles?.mainImg, boxShadow: '0 2px 16px rgba(46,139,87,0.08)' }}
                  onClick={openModal}
                />
              ) : current && current.type === 'video' ? (
                <div
                  style={{ position: 'relative', width: '100%', height: 590, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#222', borderRadius: 10, cursor: 'pointer' }}
                  onClick={openModal}
                >
                  <img
                    src={videoPreview}
                    alt="video preview"
                    style={{ width: '100%', height: 590, objectFit: 'cover', borderRadius: 10, opacity: 0.7 }}
                  />
                  <span
                    style={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      background: 'rgba(46,139,87,0.85)',
                      borderRadius: '50%',
                      width: 80,
                      height: 80,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 16px rgba(46,139,87,0.18)',
                    }}
                  >
                    <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="22" cy="22" r="22" fill="rgba(255,255,255,0.85)"/>
                      <polygon points="18,14 32,22 18,30" fill="#2e8b57" />
                    </svg>
                  </span>
                </div>
              ) : null}
              {/* Кнопки навигации */}
              {mediaItems.length > 1 && (
                <>
                  <button
                    onClick={goPrev}
                    style={{ ...bs.navBtn, left: 8, right: undefined }}
                    aria-label="Предыдущее фото"
                  >
                    ‹
                  </button>
                  <button
                    onClick={goNext}
                    style={{ ...bs.navBtn, right: 8, left: undefined }}
                    aria-label="Следующее фото"
                  >
                    ›
                  </button>
                </>
              )}
            </div>
            <div style={bs.thumbs}>
              {imgs.slice(0, 5).map((src, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentIndex(i)}
                  style={bs.thumbBtn(currentIndex === i)}
                  aria-label={`Показати фото ${i + 1}`}
                >
                  <img src={src} alt={`thumb-${i + 1}`} style={{ ...bs.thumbImg, ...fallbackStyles?.thumbImg }} />
                </button>
              ))}
              {video && (
                <button
                  onClick={() => setCurrentIndex(mediaItems.findIndex(m => m.type === 'video'))}
                  style={bs.thumbBtn(current && current.type === 'video')}
                  aria-label="Показати відео"
                >
                  <div style={{position:'relative',width:90,height:70,display:'flex',alignItems:'center',justifyContent:'center'}}>
                    <img src={imgs[0] || '/img/main2.png'} alt="video preview" style={{width:90,height:70,objectFit:'cover',borderRadius:8,opacity:0.7}} />
                    <span style={{position:'absolute',left:'50%',top:'50%',transform:'translate(-50%,-50%)',background:'rgba(46,139,87,0.85)',borderRadius:'50%',width:32,height:32,display:'flex',alignItems:'center',justifyContent:'center',boxShadow:'0 2px 8px rgba(46,139,87,0.18)'}}>
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="9" cy="9" r="9" fill="rgba(255,255,255,0.85)"/>
                        <polygon points="7,5 13,9 7,13" fill="#2e8b57" />
                      </svg>
                    </span>
                  </div>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      {modalOpen && (
        <div style={bs.modal} onClick={closeModal}>
          <div style={bs.modalContent} onClick={e => e.stopPropagation()}>
            <button
              onClick={closeModal}
              style={bs.modalClose}
              aria-label="Закрити"
            >
              ×
            </button>
            {current && current.type === 'image' ? (
              <img
                src={current.src}
                alt={`${title} — фото ${currentIndex + 1}`}
                style={{ maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: 12, background: '#fff', boxShadow: '0 2px 16px rgba(46,139,87,0.12)' }}
              />
            ) : current && current.type === 'video' ? (
              <video
                controls
                autoPlay
                style={{ maxWidth: '90vw', maxHeight: '80vh', borderRadius: 12, background: '#222', boxShadow: '0 2px 16px rgba(46,139,87,0.12)' }}
              >
                <source src={current.src} type="video/mp4" />
                Ваш браузер не підтримує відео.
              </video>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

IprokWindow.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  images: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
  video: PropTypes.string,
  price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ctaText: PropTypes.string,
  onCta: PropTypes.func
};
